import type { Optional } from 'utility-types'

import { assert } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type CybusPersistedPermission, type EditableCybusPermission } from '../../../../domain'

import { mapContextToPermissionContext, mapOperationsToResponse, mapPermissionContextToContext, mapResponseToOperations } from '../../../Connectware'
import type { Permission, PermissionPostRequest, PermissionPutRequest } from '../Types'

const mapPermissionPutRequest = (permission: EditableCybusPermission): PermissionPutRequest => {
    assert(permission.id, new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Could not map PermissionPutRequest', permission))
    return { id: permission.id, operation: mapOperationsToResponse(permission) }
}

export const mapPermissionPostRequest = (permission: EditableCybusPermission): PermissionPostRequest => {
    assert(permission.id === null, new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Could not map PermissionPostRequest', permission))
    return { context: mapPermissionContextToContext(permission.context), resource: permission.resource, operation: mapOperationsToResponse(permission) }
}

export const mapPermissionRequest = (permission: EditableCybusPermission): PermissionPutRequest | PermissionPostRequest =>
    permission.id ? mapPermissionPutRequest(permission) : mapPermissionPostRequest(permission)

const mapPermission = ({ id, context, resource, operation }: Optional<Permission, 'id'>): EditableCybusPermission => ({
    id: id || null,
    context: mapContextToPermissionContext(context),
    resource,
    ...mapResponseToOperations(operation),
})

const isCybusPersistedPermission = (permission: EditableCybusPermission): permission is CybusPersistedPermission => typeof permission.id === 'string'

export const mapEditableCybusPermissions = (permissions: readonly Optional<Permission, 'id'>[]): EditableCybusPermission[] => permissions.map(mapPermission)

export const mapPersistedPermissions = (permissions: readonly Permission[]): CybusPersistedPermission[] =>
    permissions.map((p) => {
        const out = mapPermission(p)
        assert(isCybusPersistedPermission(out), new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Id should be present', p))
        return out
    })
